const CAPTURE_APP_WS_URL = 'ws://localhost:49521'

/**
 * Audio input source used for the current AI Teammate listening session.
 */
export type TeammateAudioSource = 'capture-app' | 'mic'

/**
 * Media stream selected for OpenAI Realtime input and the source that produced it.
 */
export interface TeammateAudioSourceResult {
  stream: MediaStream
  source: TeammateAudioSource
}

/**
 * Controls microphone and native capture-app audio resources for AI Teammate.
 *
 * The widget should treat this as the only owner of capture WebSocket message
 * handlers, Web Audio nodes, and MediaStream tracks. Realtime reconnects reuse
 * the returned stream and should not call `stop()` unless the whole listening
 * session is ending.
 */
export class TeammateAudioSourceController {
  /** Persistent connection to the local native capture app ("Linkando Teammate"), when available. */
  private captureWs: WebSocket | null = null
  /** Web Audio graph used to convert capture-app PCM chunks into a MediaStream. */
  private audioContext: AudioContext | null = null
  /** Processor node that drains capture-app chunks into the MediaStream output. */
  private scriptProcessor: ScriptProcessorNode | null = null
  /** Current mic or synthesized capture-app stream owned by this controller. */
  private mediaStream: MediaStream | null = null
  /** Buffered stereo Float32 chunks received from the capture app. */
  private audioQueue: { chunks: Float32Array[]; readPos: number } = { chunks: [], readPos: 0 }
  /** True when the active stream is backed by the capture app rather than mic. */
  private usingCaptureApp = false
  /** Cleanup function for the capture-app availability probe WebSocket. */
  private stopProbe: (() => void) | null = null

  /**
   * Probe the native capture app and report whether it is connected.
   *
   * The probe WebSocket is kept open because the capture source reuses it for
   * audio frames if the user starts listening while the app is connected.
   */
  probeCaptureApp(onConnectedChange: (isConnected: boolean) => void) {
    this.stopProbe?.()

    try {
      const ws = new WebSocket(CAPTURE_APP_WS_URL)
      ws.binaryType = 'arraybuffer'

      const connectionTimeout = setTimeout(() => {
        if (ws.readyState !== WebSocket.OPEN) ws.close()
      }, 3000)

      ws.onopen = () => {
        clearTimeout(connectionTimeout)
        this.captureWs = ws
        onConnectedChange(true)
      }

      ws.onclose = () => {
        clearTimeout(connectionTimeout)
        if (this.captureWs === ws) {
          this.captureWs = null
          onConnectedChange(false)
        }
      }

      this.stopProbe = () => {
        clearTimeout(connectionTimeout)
        ws.onclose = null
        ws.onmessage = null
        ws.close()
        if (this.captureWs === ws) {
          this.captureWs = null
        }
      }
    } catch (err) {
      console.warn('Could not connect to capture app WebSocket:', err)
    }
  }

  /**
   * Start the best available audio input stream.
   *
   * Uses the capture app when its probe WebSocket is open; otherwise falls back
   * to direct microphone capture.
   */
  async startPreferredSource(): Promise<TeammateAudioSourceResult> {
    if (this.captureWs?.readyState === WebSocket.OPEN) {
      return {
        stream: await this.startCaptureAppSource(this.captureWs),
        source: 'capture-app',
      }
    }

    return {
      stream: await this.startMicSource(),
      source: 'mic',
    }
  }

  /**
   * Stop the active audio stream and any capture-app processing graph.
   *
   * This intentionally keeps the capture-app probe WebSocket open so a later
   * start can still use capture-app audio without probing again.
   */
  stop() {
    if (this.usingCaptureApp && this.captureWs?.readyState === WebSocket.OPEN) {
      this.captureWs.send(JSON.stringify({ type: 'stop-capture' }))
    }

    if (this.scriptProcessor) {
      this.scriptProcessor.disconnect()
      this.scriptProcessor = null
    }

    if (this.audioContext) {
      void this.audioContext.close()
      this.audioContext = null
    }

    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach(track => track.stop())
      this.mediaStream = null
    }

    this.audioQueue = { chunks: [], readPos: 0 }
    if (this.captureWs) {
      this.captureWs.onmessage = null
    }
    this.usingCaptureApp = false
  }

  /**
   * Stop all audio resources and close the capture-app probe.
   */
  dispose() {
    this.stop()
    this.stopProbe?.()
    this.stopProbe = null
  }

  private async startMicSource() {
    // Ensure a previous capture graph or mic stream is not still feeding audio.
    this.stop()
    this.usingCaptureApp = false

    const stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      },
    })
    this.mediaStream = stream
    return stream
  }

  private async startCaptureAppSource(ws: WebSocket) {
    // Reset any previous stream before reusing the probe WebSocket for audio.
    this.stop()
    this.usingCaptureApp = true

    const audioContext = new AudioContext({ sampleRate: 48000 })
    this.audioContext = audioContext
    this.audioQueue = { chunks: [], readPos: 0 }

    // The capture app sends stereo interleaved Float32 PCM frames.
    ws.onmessage = (event: MessageEvent) => {
      if (event.data instanceof ArrayBuffer) {
        this.audioQueue.chunks.push(new Float32Array(event.data))
      }
    }

    const scriptProcessor = audioContext.createScriptProcessor(4096, 1, 1)
    this.scriptProcessor = scriptProcessor

    scriptProcessor.onaudioprocess = (e: AudioProcessingEvent) => {
      const output = e.outputBuffer.getChannelData(0)
      const queue = this.audioQueue
      let outIdx = 0

      // Drain queued stereo frames into a mono output buffer. Silence is emitted
      // when the capture app has not provided enough frames yet.
      while (outIdx < output.length) {
        if (queue.chunks.length === 0) {
          output[outIdx++] = 0
          continue
        }

        const chunk = queue.chunks[0]
        const chunkFrames = chunk.length / 2

        if (queue.readPos >= chunkFrames) {
          queue.chunks.shift()
          queue.readPos = 0
          continue
        }

        // Capture-app chunks are interleaved L/R samples; average them to mono.
        const i = queue.readPos * 2
        output[outIdx++] = (chunk[i] + chunk[i + 1]) * 0.5
        queue.readPos++
      }
    }

    // MediaStreamDestination turns our synthetic mono audio graph into the
    // MediaStream expected by RTCPeerConnection.addTrack().
    const destination = audioContext.createMediaStreamDestination()
    scriptProcessor.connect(destination)
    const stream = destination.stream
    this.mediaStream = stream

    ws.send(JSON.stringify({ type: 'start-capture' }))
    return stream
  }
}
